/**
 * RuggedTopNav — Barra de navegação superior no estilo do mock
 * mock-admin-dashboard-rugged.html.
 *
 * Mostra:
 *   - Brand mark (T4×4) + wordmark TECTUR
 *   - Tabs admin: Dashboard · Upload · Editar · Users · Reports · Gamificação
 *     (só admin/staff vê as tabs admin)
 *   - Right: role badge + user info
 *
 * As tabs disparam callbacks para abrir os modais que já existem (Upload,
 * Users, Reports, NavChallenge, etc.). Não reescreve nada existente — só
 * adiciona a camada visual Rugged por cima.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

const styles = {
  wrap: {
    position: 'sticky',
    top: 0,
    zIndex: 40,
    background: 'linear-gradient(180deg, #0F0D0B 0%, #1A1613 100%)',
    borderBottom: '2px solid #E87A2A',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginRight: 6,
  },
  brandMark: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#E87A2A',
    color: '#0F0D0B',
    display: 'grid',
    placeItems: 'center',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10,
    fontWeight: 900,
    border: '2px solid #F2EBDD',
    letterSpacing: '0.5px',
  },
  brandName: {
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 900,
    letterSpacing: '3px',
    fontSize: 13,
    color: '#F2EBDD',
  },
  tab: {
    padding: '7px 12px',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    background: 'transparent',
    color: 'rgba(242, 235, 221, 0.55)',
    border: '1px solid rgba(196, 162, 117, 0.20)',
    borderRadius: 6,
    fontFamily: "'JetBrains Mono', monospace",
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: '#E87A2A',
    color: '#0F0D0B',
    borderColor: '#E87A2A',
  },
  meta: {
    marginLeft: 'auto',
    fontSize: 10,
    color: 'rgba(242, 235, 221, 0.30)',
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  roleBadge: {
    padding: '3px 8px',
    borderRadius: 4,
    background: '#E87A2A',
    color: '#0F0D0B',
    fontWeight: 900,
    fontSize: 9,
    letterSpacing: '1.5px',
  },
};

export default function RuggedTopNav({ user, activeTab = 'dashboard', onTabChange, draftCount = 0, errorCount = 0 }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const canAdmin = isAdmin || isStaff;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', always: true },
    { id: 'upload', label: 'Upload Rota', staffOrAdmin: true },
    { id: 'drafts', label: 'Rascunhos', adminOnly: true, badge: draftCount },
    { id: 'ranking', label: 'Ranking Card', adminOnly: true },
    { id: 'errors', label: 'Erros', adminOnly: true, badge: errorCount, badgeColor: '#D67373' },
    { id: 'empresas', label: 'Empresas', adminOnly: true, highlight: true },
    { id: 'municipios', label: 'Municípios', adminOnly: true, highlight: true },
    { id: 'users', label: 'Users', adminOnly: true },
    { id: 'reports', label: 'Reports', staffOrAdmin: true },
    { id: 'gamification', label: 'Gamificação', always: true },
    // Painéis próprios (municípios / empresas) — entram a partir do principal com mapa.
    { id: 'my-municipio', label: 'Município', highlight: true, show: (u) => u?.role === 'municipio' && !!u?.municipality_id },
    { id: 'my-company', label: 'Empresa', highlight: true, show: (u) => !!u?.company_id && u?.role !== 'admin' },
  ];

  const tabHandler = (id) => {
    if (typeof onTabChange === 'function') onTabChange(id);
  };

  return (
    <nav style={styles.wrap} className="rugged-top-nav" data-testid="rugged-top-nav">
      <div style={styles.brand}>
        <div style={styles.brandMark}>T4×4</div>
        <div>
          <div style={styles.brandName}>TECTUR</div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 8,
            color: '#E87A2A',
            letterSpacing: '2.5px',
            marginTop: 1,
          }}>
            NAVIGATION · OFF-ROAD
          </div>
        </div>
      </div>

      {tabs.map((t) => {
        if (t.adminOnly && !isAdmin) return null;
        if (t.staffOrAdmin && !canAdmin) return null;
        if (t.show && !t.show(user)) return null;
        const isActive = activeTab === t.id;
        const highlightStyle = t.highlight && !isActive
          ? { color: '#E87A2A', borderColor: '#E87A2A', background: 'rgba(232,122,42,0.08)' }
          : {};
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => tabHandler(t.id)}
            data-testid={`nav-tab-${t.id}`}
            style={{
              ...styles.tab,
              ...highlightStyle,
              ...(isActive ? styles.tabActive : {}),
            }}
            onMouseOver={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = '#F2EBDD';
                e.currentTarget.style.borderColor = '#C4A275';
              }
            }}
            onMouseOut={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = 'rgba(242, 235, 221, 0.55)';
                e.currentTarget.style.borderColor = 'rgba(196, 162, 117, 0.20)';
              }
            }}
          >
            {t.label}
            {t.badge > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  padding: '1px 6px',
                  borderRadius: 10,
                  background: isActive ? '#0F0D0B' : (t.badgeColor || '#E87A2A'),
                  color: isActive ? (t.badgeColor || '#E87A2A') : '#0F0D0B',
                  fontSize: 9,
                  fontWeight: 900,
                  letterSpacing: 0,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
                data-testid={`nav-tab-${t.id}-badge`}
              >
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </button>
        );
      })}

      <div style={styles.meta} className="rugged-top-nav-meta">
        {canAdmin && <span style={styles.roleBadge}>{isAdmin ? 'ADMIN' : 'STAFF'}</span>}
        <span>{user?.email || user?.name || ''}</span>
      </div>
    </nav>
  );
}
